import { emptyWorkout, type WorkoutContent } from '@tg/domain';
import type { QueryClient } from '@tanstack/react-query';

/* Дни тренировок на устройстве: как правка из очереди меняет строки дней (до ответа сервера),
   и ответы на запросы дней без сети — из того, что уже на устройстве. */

export type Who = { clientId: string } | { groupId: string };
export const whoKey = (w: Who) => ('clientId' in w ? 'c:' + w.clientId : 'g:' + w.groupId);
export const dayKey = (w: Who, date: string) => whoKey(w) + '@' + date;

/** Строка дня, как её отдаёт trainer.days */
export interface DayRow {
  id: string;
  date: string;
  content: WorkoutContent;
  work: WorkoutContent | null;
  status: 'draft' | 'published';
  fromGroupId: string | null;
  edited: boolean;
  rev: number;
  stat: null | { has: number; total: number };
}
/** Строка дня в offlinePack — то же плюс чей день */
export type PackRow = Omit<DayRow, 'stat'> & { clientId: string | null; groupId: string | null };
export interface Pack {
  from: string;
  to: string;
  days: PackRow[];
  maxes: Record<string, Record<string, number>>;
  members: Record<string, string[]>;
}

/** Операции с днём, которые работают без сети */
export type DayOp =
  | { op: 'editDay'; input: { who: Who; date: string; content: WorkoutContent } }
  | { op: 'saveDays'; input: { who: Who; dates?: string[] | null; decisions?: unknown } }
  | { op: 'setStatus'; input: { who: Who; date: string; published: boolean; decisions?: unknown } };

const sameWho = (a: Who, b: Who) => whoKey(a) === whoKey(b);
const rowWho = (r: PackRow): Who => (r.clientId ? { clientId: r.clientId } : { groupId: r.groupId! });

/** Применить операцию к строкам дней одного человека/группы (копия; range — какие даты в этом списке) */
export function applyOp<R extends Omit<DayRow, 'stat'>>(rows: R[], o: DayOp, make: (date: string) => R, range?: { from: string; to: string }): R[] {
  const out = rows.slice();
  if (o.op === 'editDay') {
    const i = out.findIndex((r) => r.date === o.input.date);
    if (i >= 0) out[i] = { ...out[i], work: o.input.content };
    else if (!range || (o.input.date >= range.from && o.input.date <= range.to)) {
      out.push({ ...make(o.input.date), work: o.input.content });
      out.sort((a, b) => (a.date < b.date ? -1 : 1));
    }
    return out;
  }
  if (o.op === 'saveDays') {
    const only = o.input.dates ? new Set(o.input.dates) : null;
    return out.map((r) => (r.work && (!only || only.has(r.date)) ? { ...r, content: r.work, work: null } : r));
  }
  return out.map((r) => (r.date === o.input.date ? { ...r, content: r.work ?? r.content, work: null, status: o.input.published ? 'published' : 'draft' } : r));
}

const blankRow = (date: string): DayRow => ({ id: 'local:' + date, date, content: emptyWorkout(), work: null, status: 'draft', fromGroupId: null, edited: false, rev: 0, stat: null });

/** Ответ trainer.days с правками из очереди поверх */
export function rebaseDays(rows: DayRow[], input: { who: Who; from: string; to: string }, ops: DayOp[]) {
  let out = rows;
  for (const o of ops) if (sameWho(o.input.who, input.who)) out = applyOp(out, o, blankRow, input);
  return out;
}

/** Ответ trainer.offlinePack с правками из очереди поверх */
export function rebasePack(p: Pack, ops: DayOp[]): Pack {
  if (!ops.length) return p;
  let days = p.days;
  for (const o of ops) {
    const w = o.input.who;
    const mine = days.filter((r) => sameWho(rowWho(r), w));
    const rest = days.filter((r) => !sameWho(rowWho(r), w));
    const make = (date: string): PackRow => {
      const { stat: _s, ...b } = blankRow(date);
      return { ...b, clientId: 'clientId' in w ? w.clientId : null, groupId: 'groupId' in w ? w.groupId : null };
    };
    days = [...rest, ...applyOp(mine, o, make, p)];
  }
  return { ...p, days };
}

export const PACK_PATH = ['trainer', 'offlinePack'];
const DAYS_PATH = 'trainer.days';
const pathOf = (key: readonly unknown[]) => (Array.isArray(key[0]) ? (key[0] as string[]).join('.') : '');
const inputOf = (key: readonly unknown[]) => (key[1] as { input?: unknown } | undefined)?.input;

/** Правка поставлена в очередь — сразу видна во всех открытых списках дней и в данных на устройстве */
export function patchCaches(qc: QueryClient, o: DayOp) {
  for (const q of qc.getQueryCache().findAll({ queryKey: [['trainer', 'days']] })) {
    const inp = inputOf(q.queryKey) as { who: Who; from: string; to: string } | undefined;
    if (!inp || !sameWho(inp.who, o.input.who) || !q.state.data) continue;
    qc.setQueryData(q.queryKey, rebaseDays(q.state.data as DayRow[], inp, [o]));
  }
  for (const q of qc.getQueryCache().findAll({ queryKey: [PACK_PATH] })) if (q.state.data) qc.setQueryData(q.queryKey, rebasePack(q.state.data as Pack, [o]));
}

/** Данные на устройстве для работы без сети: самое свежее окно дней */
export function packOf(qc: QueryClient): Pack | undefined {
  let best: { at: number; p: Pack } | undefined;
  for (const q of qc.getQueryCache().findAll({ queryKey: [PACK_PATH] }))
    if (q.state.data && (!best || q.state.dataUpdatedAt > best.at)) best = { at: q.state.dataUpdatedAt, p: q.state.data as Pack };
  return best?.p;
}

/** Ответ на запрос без сети из данных на устройстве; undefined — ответить нечем */
export function synth(qc: QueryClient, path: string, input: unknown): unknown {
  /* Тот же запрос уже есть на устройстве (с правками очереди) — он и ответ */
  const exact = qc.getQueryData([path.split('.'), input === undefined ? { type: 'query' } : { input, type: 'query' }]);
  if (exact !== undefined) return exact;
  const p = packOf(qc);
  if (!p) return undefined;
  if (path === DAYS_PATH) {
    const i = input as { who: Who; from: string; to: string };
    return p.days
      .filter((r) => sameWho(rowWho(r), i.who) && r.date >= i.from && r.date <= i.to)
      .map(({ clientId: _c, groupId: _g, ...r }): DayRow => ({ ...r, stat: null }));
  }
  if (path === 'trainer.maxesFor') {
    const w = input as Who;
    const ids = 'clientId' in w ? [w.clientId] : p.members[w.groupId] || [];
    return Object.fromEntries(ids.filter((id) => p.maxes[id]).map((id) => [id, p.maxes[id]]));
  }
  if (path === 'trainer.lastBefore') {
    const i = input as { who: Who; date: string };
    const ds = p.days.filter((r) => sameWho(rowWho(r), i.who) && r.date < i.date && r.content.blocks.length > 0).map((r) => r.date);
    return ds.length ? ds.reduce((a, b) => (a > b ? a : b)) : null;
  }
  return undefined;
}

export { pathOf, inputOf };
